/**
 * useKiosk (B6): the kiosk's idle life, spec §G and §A.5.
 *
 *   useKiosk({ choreo, enabled: kiosk });                       // 90 s → attract, 180 s → reset
 *   useKiosk({ choreo, enabled, idleMs: 8000, resetMs: 30000 }); // harness timings
 *
 * - Input (window, capture phase): pointerdown, touchstart, wheel, keydown and pointermove count as
 *   activity. The idle clock only runs in phase "explore" (never over the loader, the intro or a drive).
 * - Idle → KioskController enters attract: phase "attract", bus "idle:attract", the §G loop, captions
 *   for <AttractOverlay/> (attractState).
 * - Wake (a touch, a key or the wheel; a pointer move never wakes, so a mouse's first click is the one
 *   consumed): the first touch is consumed. The whole waking gesture (down, moves, up, the click that
 *   follows), the waking key (down and up) or the waking wheel burst never reaches the map or the UI.
 *   Then choreo.cancel() (the current bloom or selection settles and stays), phase "explore",
 *   bus "idle:wake".
 * - Reset at resetMs without input (next scene boundary): resetAll(), bus "idle:reset", scene 0 (home).
 * - Cursor: hidden after 3 s without a pointer move (html.ld-loc-kiosk-nocursor), back on the next move.
 *
 * StrictMode-safe: every effect run builds its own controller and removes everything it added.
 */
import { useEffect, useRef } from "react";
import type { Choreographer } from "@/contracts/map";
import type { KioskOptions } from "@/contracts/ui";
import { bus, on } from "@/store/bus";
import { actions, appStore, getApp } from "@/store/store";
import { attractState } from "./attractState";
import { KioskController, type KioskInput, type KioskState } from "./kioskController";
import { ATTRACT } from "./script";
import "./kiosk.css";

/** Cursor hides after this long without a pointer move. */
export const CURSOR_IDLE_MS = 3000;
const NOCURSOR_CLASS = "ld-loc-kiosk-nocursor";
/** After the waking pointer lifts, the click (or synthetic mouse events) that follows is eaten too. */
const AFTER_UP_MS = 450;
/** A waking wheel burst is eaten until it pauses this long. */
const WHEEL_BURST_MS = 400;
/** Safety: a waking gesture whose pointerup never arrives stops being swallowed after this. */
const GESTURE_MAX_MS = 4000;

let current: KioskController | null = null;

/** QA and harness access to the live kiosk controller (window.__ld / window.__kiosk). */
export const kioskDebug = {
  state: (): KioskState => current?.state ?? "off",
  idleFor: (): number => current?.idleFor() ?? 0,
  forceAttract: (): void => current?.forceAttract(),
  forceResetDue: (): void => current?.forceResetDue(),
  /** Wake as a visitor's touch would (nothing to consume). */
  wake: (): boolean => current?.input("pointerdown") ?? false,
};

export function useKiosk({ choreo, enabled, idleMs = ATTRACT.idleMs, resetMs = ATTRACT.resetMs }: KioskOptions): void {
  const choreoRef = useRef<Choreographer | null>(choreo);
  useEffect(() => {
    choreoRef.current = choreo;
  }, [choreo]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;
    const ctl = new KioskController(
      {
        getState: getApp,
        subscribe: (fn) => appStore.subscribe(fn),
        actions,
        choreo: () => choreoRef.current,
        on,
        emit: (type) => bus.emit(type),
        caption: (caption, scene, loop) => attractState.set({ caption, scene, loop }),
        reload: () => window.location.reload(),
      },
      { idleMs, resetMs },
    );
    current = ctl;
    ctl.enable();

    /* ── consuming the waking input ── */
    let gesture = false; //     the waking pointer is still down
    let swallowUntil = 0; //    after it lifted: eat the click that follows
    let wheelUntil = 0;
    let wakeKey: string | null = null;
    let gestureTimer: number | undefined;
    const now = () => performance.now();
    const eat = (e: Event) => {
      if (e.cancelable) e.preventDefault();
      e.stopImmediatePropagation();
    };
    const endGesture = () => {
      gesture = false;
      swallowUntil = now() + AFTER_UP_MS;
      window.clearTimeout(gestureTimer);
    };
    const swallowing = () => gesture || now() < swallowUntil;

    const onPointerStart = (kind: KioskInput) => (e: Event) => {
      if (swallowing()) return eat(e);
      if (!ctl.input(kind)) return;
      eat(e);
      gesture = true;
      window.clearTimeout(gestureTimer);
      gestureTimer = window.setTimeout(endGesture, GESTURE_MAX_MS);
    };
    const onPointerEnd = (e: Event) => {
      if (!swallowing()) return;
      eat(e);
      if (gesture) endGesture();
    };
    const onFollow = (e: Event) => {
      if (!swallowing()) return;
      eat(e);
      if (e.type === "click") swallowUntil = 0;
    };
    const onWheel = (e: Event) => {
      if (now() < wheelUntil) {
        wheelUntil = now() + WHEEL_BURST_MS;
        return eat(e);
      }
      if (ctl.input("wheel")) {
        wheelUntil = now() + WHEEL_BURST_MS;
        eat(e);
      }
    };
    const onKeyDown = (e: Event) => {
      if (ctl.input("keydown")) {
        wakeKey = (e as KeyboardEvent).key;
        eat(e);
      }
    };
    const onKeyUp = (e: Event) => {
      if (wakeKey !== null && (e as KeyboardEvent).key === wakeKey) {
        wakeKey = null;
        eat(e);
      }
    };

    /* ── the cursor rests after 3 s ── */
    const root = document.documentElement;
    let lastMove = now();
    let cursorTimer: number | undefined;
    const armCursor = () => {
      cursorTimer = window.setTimeout(
        () => {
          cursorTimer = undefined;
          if (now() - lastMove >= CURSOR_IDLE_MS - 16) root.classList.add(NOCURSOR_CLASS);
          else armCursor();
        },
        Math.max(0, CURSOR_IDLE_MS - (now() - lastMove)),
      );
    };
    const onMove = (e: Event) => {
      lastMove = now();
      root.classList.remove(NOCURSOR_CLASS);
      if (cursorTimer === undefined) armCursor();
      if (swallowing()) return eat(e);
      ctl.input("pointermove");
    };
    armCursor();

    const listeners: Array<[string, (e: Event) => void]> = [
      ["pointerdown", onPointerStart("pointerdown")],
      ["touchstart", onPointerStart("touchstart")],
      ["pointerup", onPointerEnd],
      ["pointercancel", onPointerEnd],
      ["touchend", onPointerEnd],
      ["touchcancel", onPointerEnd],
      ["touchmove", onFollow],
      ["mousedown", onFollow],
      ["mouseup", onFollow],
      ["click", onFollow],
      ["dblclick", onFollow],
      ["contextmenu", onFollow],
      ["pointermove", onMove],
      ["wheel", onWheel],
      ["keydown", onKeyDown],
      ["keyup", onKeyUp],
    ];
    const opts: AddEventListenerOptions = { capture: true, passive: false };
    for (const [type, fn] of listeners) window.addEventListener(type, fn, opts);

    return () => {
      for (const [type, fn] of listeners) window.removeEventListener(type, fn, opts);
      window.clearTimeout(gestureTimer);
      window.clearTimeout(cursorTimer);
      root.classList.remove(NOCURSOR_CLASS);
      ctl.disable();
      if (current === ctl) current = null;
      attractState.reset();
    };
  }, [enabled, idleMs, resetMs]);
}
